import { CloseOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import ImageThumbnail from 'components/atoms/ImageThumbnail';
import ImageViewer from 'components/molecules/ImageViewer';
import { useCallback, useEffect, useState } from 'react';

import * as S from './ImageModal.styles';

interface ImageModalProps {
  images: string[];
  gfpNumbers?: string[];
  isOpen: boolean;
  title?: string;
  onClose: () => void;
}

export default function ImageModal({
  images,
  gfpNumbers,
  isOpen,
  title = 'Imagens da oferta',
  onClose,
}: ImageModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (isOpen) setCurrentIndex(0);
  }, [isOpen]);

  const goToPrevious = useCallback(
    () => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev)),
    [],
  );

  const goToNext = useCallback(
    () =>
      setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : prev)),
    [images.length],
  );

  const goToImage = useCallback((index: number) => setCurrentIndex(index), []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      const keyActions = {
        ArrowLeft: goToPrevious,
        ArrowRight: goToNext,
        Escape: onClose,
      } as const;

      const action = keyActions[event.key as keyof typeof keyActions];
      action?.();
    },
    [isOpen, goToPrevious, goToNext, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen || !images.length) return null;

  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < images.length - 1;

  return (
    <>
      <S.ModalGlobalStyles />
      <Modal
        visible={isOpen}
        onCancel={onClose}
        footer={null}
        width="95vw"
        style={{ maxWidth: '1100px', top: '5vh' }}
        centered={false}
        closable={false}
        destroyOnClose
        className="image-gallery-modal"
      >
        <S.Container className="container">
          <S.Header>
            <S.Title>{title}</S.Title>
            <S.CloseButton onClick={onClose}>
              <CloseOutlined />
            </S.CloseButton>
          </S.Header>

          <ImageViewer
            src={images[currentIndex]}
            alt={`Imagem ${currentIndex + 1}`}
            currentIndex={currentIndex}
            totalImages={images.length}
            onPrevious={goToPrevious}
            onNext={goToNext}
            canGoBack={canGoBack}
            canGoForward={canGoForward}
          />

          {gfpNumbers && gfpNumbers[currentIndex] && (
            <S.GfpNumberLabel>
              Imagem referente a GFP:{' '}
              <S.GfpNumberSpan> {gfpNumbers[currentIndex]}</S.GfpNumberSpan>
            </S.GfpNumberLabel>
          )}

          {images.length > 1 && (
            <S.ThumbnailsContainer>
              <S.ThumbnailsList>
                {images.map((image, index) => (
                  <ImageThumbnail
                    key={`thumb-${index}`}
                    src={image}
                    alt={`Miniatura ${index + 1}`}
                    isActive={index === currentIndex}
                    onClick={() => goToImage(index)}
                  />
                ))}
              </S.ThumbnailsList>
            </S.ThumbnailsContainer>
          )}
        </S.Container>
      </Modal>
    </>
  );
}
